import React from 'react';
import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, IconButton, Avatar, Menu, MenuItem, Divider, Badge } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import BusinessIcon from '@mui/icons-material/Business';
import HubIcon from '@mui/icons-material/Hub';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InventoryIcon from '@mui/icons-material/Inventory';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import ErrorIcon from '@mui/icons-material/Error';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmailIcon from '@mui/icons-material/Email';
import CategoryIcon from '@mui/icons-material/Category';
import LabelIcon from '@mui/icons-material/Label';
import FactoryIcon from '@mui/icons-material/Factory';
import TuneIcon from '@mui/icons-material/Tune';
import RouterIcon from '@mui/icons-material/Router';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SyncIcon from '@mui/icons-material/Sync';
import LogoutIcon from '@mui/icons-material/Logout';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BoltIcon from '@mui/icons-material/Bolt';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { systemApi, beaconConcernsApi } from '../api/services';
import { ENV } from '../api/config';
import { useAuth } from '../contexts/AuthContext';
import { isKioskMode } from '../utils/kioskMode';

const drawerWidth = 240;

const drawerItemsTop = [
  { text: 'Fleet', path: '/fleet', icon: <DashboardIcon /> },
  { text: 'Organizations', path: '/organizations', icon: <BusinessIcon /> },
  { text: 'Meshes', path: '/meshes', icon: <HubIcon /> },
  { text: 'Locations', path: '/locations', icon: <LocationOnIcon /> },
  { text: 'Devices', path: '/devices', icon: <RouterIcon /> },
  { text: 'Products', path: '/products', icon: <InventoryIcon /> },
  { text: 'Tag Assignments', path: '/beacon_assignments', icon: <BluetoothIcon /> },
  { text: 'Location Product Assignments', path: '/location_assignments', icon: <AssignmentIcon /> },
  { text: 'Tag Concerns', path: '/beacon_concerns', icon: <ErrorIcon /> },
  { text: 'Edge Account Digests', path: '/edge_account_digests', icon: <EmailIcon /> },
  { text: 'Edge Reviews', path: '/outcomes', icon: <FactCheckIcon /> },
];

// Edge Onboard — staff side of the customer onboarding app (SC-5912).
// Ordered by the setup flow: create the user → create the go-live (the dialog
// enforces the HQ-link prerequisite) → load tag slips.
const drawerItemsOnboarding = [
  { text: 'Customer Users', path: '/onboarding/users', icon: <PeopleIcon /> },
  { text: 'Go-Lives', path: '/onboarding/go_lives', icon: <BoltIcon /> },
  { text: 'Tag Slips', path: '/onboarding/tag_slips', icon: <ReceiptLongIcon /> },
  { text: 'Staff Guide', path: '/onboarding/guide', icon: <MenuBookIcon /> },
];

const drawerItemsBottom = [
  { text: 'Product Equivalencies', path: '/product_equivalencies', icon: <CategoryIcon /> },
  { text: 'Location PE Assignments', path: '/location_equivalencies', icon: <TuneIcon /> },
  { text: 'Manufacturers', path: '/manufacturers', icon: <FactoryIcon /> },
  { text: 'Equivalency Categories', path: '/product_equivalency_categories', icon: <LabelIcon /> },
  { text: 'HexaHQ Sync', path: '/cache_management', icon: <SyncIcon /> },
];

interface LayoutProps {
  children: React.ReactNode;
}

// Kiosk mode strips all chrome: no AppBar (logout menu, env name), no Drawer
// (sidebar nav), no footer. Just the dashboard content edge-to-edge. Split
// into its own component so FullLayout's hooks aren't conditionally invoked.
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  if (isKioskMode()) {
    return <>{children}</>;
  }
  return <FullLayout>{children}</FullLayout>;
};

const FullLayout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [version, setVersion] = React.useState<string | null>(null);
  const [apiVersion, setApiVersion] = React.useState<string | null>(null);

  const getHeaderTitle = () => {
    const appName = 'Edge Admin';
    
    if (ENV.isProduction) {
      return appName;
    }
    
    // For non-production environments, show environment prefix
    const envName = ENV.current.toUpperCase();
    return `${envName} - ${appName}`;
  };

  React.useEffect(() => {
    // Set document title based on environment
    document.title = getHeaderTitle();

    fetch(process.env.PUBLIC_URL + '/version.txt')
      .then(res => res.ok ? res.text() : null)
      .then(text => setVersion(text?.trim() || null))
      .catch(() => setVersion(null));

    systemApi.getVersion()
      .then(setApiVersion)
      .catch(() => setApiVersion(null));
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/login');
  };

  const { data: concernSummary } = useQuery({
    queryKey: ['beacon-concerns-summary'],
    queryFn: beaconConcernsApi.summary,
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const openConcernCount = concernSummary?.open ?? 0;

  const drawer = (
    <div>
      <Toolbar />
      <List>
        {drawerItemsTop.map((item) => (
          <ListItemButton
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>
              {item.path === '/beacon_concerns' ? (
                <Badge badgeContent={openConcernCount} color="error" max={99} invisible={openConcernCount === 0}>
                  {item.icon}
                </Badge>
              ) : item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <List
        subheader={
          <ListSubheader component="div" sx={{ lineHeight: '32px', mt: 0.5 }}>
            Edge Onboard
          </ListSubheader>
        }
      >
        {drawerItemsOnboarding.map((item) => (
          <ListItemButton
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <List>
        {drawerItemsBottom.map((item) => (
          <ListItemButton
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {getHeaderTitle()}
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user.name}
              </Typography>
              <IconButton
                size="small"
                onClick={handleUserMenuOpen}
                sx={{ ml: 1 }}
              >
                <Avatar
                  src={user.picture}
                  sx={{ width: 32, height: 32 }}
                >
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user.email}</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar /> {/* Spacer for AppBar */}
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </Box>
      <Box component="footer" sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper', mt: 'auto' }}>
        <Typography variant="body2" color="textSecondary">
          {version || apiVersion ? `Admin: ${version || 'N/A'}, API: ${apiVersion || 'N/A'}` : ''}
        </Typography>
      </Box>
    </Box>
  );
}; 